import { queryClient } from '@/router'
import { useMutation, useSuspenseQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import { API_ENDPOINTS } from '@/lib/constants/api'
import { useDialog } from '@/lib/hooks/use-dialog'
import { deleteCategoryMutation } from '@/lib/queries/business/v1/case/path/mutations'
import { getAllCategoryListQuery } from '@/lib/queries/business/v1/case/path/queries'

import { type CategoryListProps } from './category-list'

export type UseCategoryListParams = Pick<CategoryListProps, 'onSuccessDeleteCategory'>

export const useCategoryList = ({ onSuccessDeleteCategory }: UseCategoryListParams) => {
  const { data: dataCategories } = useSuspenseQuery(getAllCategoryListQuery)
  const { mutateAsync: deleteCategory, isPending: isDeleteCategoryPending } = useMutation(deleteCategoryMutation)

  const [selectedCategoryIds, setSelectedCategoryIds] = useState<Set<string>>(new Set())

  const {
    isDialogOpen: isDeleteConfirmDialogOpen,
    openDialog: openDeleteConfirmDialog,
    closeDialog: closeDeleteConfirmDialog,
  } = useDialog()

  const selectedCategories = dataCategories?.filter(({ _id }) => selectedCategoryIds.has(_id))
  const isDeleteButtonDisabled = selectedCategoryIds.size === 0

  const toggleItem = (id: string) => {
    const newSelectedCategoryIds = new Set(selectedCategoryIds)

    if (newSelectedCategoryIds.has(id)) {
      newSelectedCategoryIds.delete(id)
    } else {
      newSelectedCategoryIds.add(id)
    }

    setSelectedCategoryIds(newSelectedCategoryIds)
  }

  const handleConfirmDelete = async () => {
    const ids = Array.from(selectedCategoryIds)
    const allTargetCategoryIds = ids
      .map((id) => dataCategories.find(({ _id }) => id === _id)?.ids)
      .filter((v) => v)
      .flat() as number[]

    await deleteCategory(
      { ids: allTargetCategoryIds },
      {
        onSuccess: () => {
          toast.success(`${selectedCategoryIds.size > 1 ? 'Categories' : 'Category'} deleted successfully.`)
          setTimeout(() => {
            void queryClient.invalidateQueries({
              queryKey: [API_ENDPOINTS.business.v1.case.path.index],
            })
          }, 2000)
          onSuccessDeleteCategory()
        },
        onError: () => {
          toast.error(`Failed to delete ${selectedCategoryIds.size > 1 ? 'categories' : 'category'}.`)
        },
      }
    )
  }

  return {
    dataCategories,
    selectedCategoryIds,
    selectedCategories,
    isDeleteButtonDisabled,
    isDeleteConfirmDialogOpen,
    isDeleteCategoryPending,
    toggleItem,
    openDeleteConfirmDialog,
    closeDeleteConfirmDialog,
    handleConfirmDelete,
  }
}
